import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
const API_URL = process.env.REACT_APP_API_URL || '';

const ESignPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [letterData, setLetterData] = useState(null);
  const [error, setError] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [signatureEmpty, setSignatureEmpty] = useState(true);

  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);

  useEffect(() => {
    const fetchLetter = async () => {
      try {
        const res = await fetch(`${API_URL}/api/users/esign/${token}`);
        const data = await res.json();
        if (data.success) {
          setLetterData(data.data);
          if (data.data.esign_status === 'completed') setCompleted(true);
        } else {
          setError(data.message || 'Invalid or expired eSign link.');
        }
      } catch (err) {
        console.error('Failed to fetch eSign letter', err);
        setError('Failed to connect to the server.');
      } finally {
        setLoading(false);
      }
    };
    fetchLetter();
  }, [token]);

  // Initialize canvas background
  useEffect(() => {
    if (!canvasRef.current || completed) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [completed, loading]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    isDrawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  }, []);

  const draw = useCallback((e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
    setSignatureEmpty(false);
  }, []);

  const stopDraw = useCallback(() => {
    isDrawing.current = false;
    lastPos.current = null;
  }, []);

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureEmpty(true);
  };

  const handleSubmit = async () => {
    if (!agreed) {
      alert('Please agree to electronically sign the document to proceed.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/users/esign/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signatureImage: "" }),
      });
      const data = await res.json();
      if (data.success) {
        setCompleted(true);
        const res2 = await fetch(`${API_URL}/api/users/esign/${token}`);
        const data2 = await res2.json();
        if (data2.success) setLetterData(data2.data);
      } else {
        alert(data.message || 'Failed to submit eSign.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
        <p style={{ fontSize: '18px', color: '#64748b' }}>Loading document securely...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc', padding: '20px' }}>
        <div style={{ background: '#fff', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '500px' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px' }}>
            <circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <h2 style={{ margin: '0 0 16px 0', color: '#1e293b' }}>Access Denied</h2>
          <p style={{ color: '#64748b', marginBottom: '24px' }}>{error}</p>
          <button onClick={() => navigate('/')} style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '10px 24px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f1f5f9' }}>
      <header style={{ background: '#fff', padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '20px', color: '#0f172a' }}>Document eSign</h1>
          <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#64748b' }}>Review and sign your Sanction Letter</p>
        </div>
        {completed && (
          <div style={{ background: '#ecfdf5', color: '#047857', padding: '6px 12px', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            eSign Completed
          </div>
        )}
      </header>

      <main style={{ flex: 1, overflow: 'auto', padding: '24px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: '850px', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {letterData?.letter_html && (
            <iframe
              srcDoc={letterData.letter_html}
              style={{ width: '100%', height: 'calc(100vh - 280px)', border: 'none', background: '#fff' }}
              title="Sanction Letter"
            />
          )}
        </div>
      </main>

      {!completed && (
        <footer style={{ background: '#fff', padding: '24px', borderTop: '1px solid #e2e8f0', boxShadow: '0 -4px 12px rgba(0,0,0,0.05)' }}>
          <div style={{ maxWidth: '850px', margin: '0 auto' }}>

            {/* Agreement checkbox */}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', marginBottom: '20px' }}>
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                style={{ width: '20px', height: '20px', marginTop: '2px', accentColor: '#10b981' }}
              />
              <span style={{ fontSize: '15px', color: '#334155', lineHeight: '1.5' }}>
                I agree to electronically sign this Sanction Letter. I understand that checking this box constitutes a legal signature confirming that I acknowledge and agree to the terms and conditions outlined in this document. My Aadhaar details will be recorded as part of this electronic signature.
              </span>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleSubmit}
                disabled={!agreed || submitting}
                style={{
                  background: agreed && !submitting ? '#10b981' : '#94a3b8',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 32px',
                  borderRadius: '8px',
                  cursor: agreed && !submitting ? 'pointer' : 'not-allowed',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  transition: 'background 0.2s',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit eSign'}
              </button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default ESignPage;
